/**
 * Mode dégradé (CP-12) — tenir le coffre du poste à jour tant que le réseau
 * est là, pour qu'il serve le jour où il n'y est plus.
 *
 * La clé se dérive du mot de passe **au moment de la connexion**, seul instant
 * où la plateforme le voit ; elle reste en mémoire (non extractible), jamais
 * sur le disque. Un rechargement de page la perd : le coffre existant reste
 * lisible hors ligne, il n'est simplement plus rafraîchi avant la prochaine
 * connexion.
 */
import { schemaReferentielHorsLigne, type ReferentielHorsLigne } from '@afs/shared'
import { requete } from '../../shared/api/client.js'
import { estErreurApi } from '../../shared/api/erreurs.js'
import { deriverCle, nouveauSel, ouvrirAvecCle, sceller, type Octets } from './coffre.js'
import { cleDe, lireCoffre, rangerCoffre, type CoffreRange } from './depot.js'

/** Entre deux rafraîchissements du coffre, tant que la session est ouverte. */
export const INTERVALLE_SYNCHRO_MS = 30 * 60_000

/** Un 429 sans `Retry-After` lisible : on attend cinq minutes, pas moins (S314). */
export const DELAI_REESSAI_PAR_DEFAUT_MS = 5 * 60_000

interface CleEnMemoire {
  readonly email: string
  readonly cle: CryptoKey
  readonly sel: Octets
  /** Le coffre du poste s'ouvre avec cette clé : vrai une fois vérifié ou scellé par elle. */
  ouvreLeCoffre: boolean
}

let enMemoire: CleEnMemoire | null = null
/**
 * La dérivation en cours (310 000 itérations, une seconde sur un poste lent).
 * La coque peut demander le coffre avant qu'elle finisse : elle l'attend, au
 * lieu de conclure « pas de clé » et d'attendre trente minutes (S314).
 */
let preparation: Promise<void> | null = null
/**
 * Une clé neuve à chaque connexion (S396) : un coffre jugé récent sous la clé
 * précédente — mot de passe changé, par exemple — se revérifie avec la neuve.
 */
let generation = 0

export function generationCle(): number {
  return generation
}

/**
 * À appeler dès que le serveur a accepté le mot de passe. Ne lève jamais : une
 * erreur ici ne doit pas empêcher quelqu'un de se connecter.
 */
export function preparerCle(email: string, motDePasse: string): Promise<void> {
  generation += 1
  preparation = (async () => {
    try {
      // Le sel du coffre existant est repris : le même mot de passe rouvre le même coffre.
      const existant = await lireCoffre(email)
      const sel = existant?.coffre.sel ?? nouveauSel()
      const cle = await deriverCle(motDePasse, sel)
      enMemoire = { email: cleDe(email), cle, sel, ouvreLeCoffre: false }
    } catch {
      enMemoire = null
    }
  })()
  return preparation
}

export function oublierCle(): void {
  enMemoire = null
  preparation = null
}

export function cleCourante(email: string): CryptoKey | null {
  return enMemoire?.email === cleDe(email) ? enMemoire.cle : null
}

export interface Agent {
  readonly email: string
  readonly nomAffiche: string
  readonly siteNom: string | null
}

/** Ce qu'a donné une synchronisation ; `coffre` dit si l'agent a quand même un coffre sur ce poste. */
export type ResultatSynchro =
  | { readonly etat: 'rangee' }
  | { readonly etat: 'fraiche' }
  | { readonly etat: 'sans-cle'; readonly coffre: boolean }
  | { readonly etat: 'limite'; readonly coffre: boolean; readonly reessayerDansMs: number }
  | { readonly etat: 'echec'; readonly coffre: boolean }

/** Scellé il y a moins de trente minutes : le retélécharger ne servirait à rien. */
function estFrais(range: CoffreRange, maintenant: number): boolean {
  const age = maintenant - new Date(range.scelleLe).getTime()
  return age >= 0 && age < INTERVALLE_SYNCHRO_MS
}

/**
 * Un coffre frais scellé sous un autre mot de passe (changé depuis) ne
 * s'ouvrirait pas à la coupure : il se retélécharge. Vérifié une fois par clé.
 */
async function ouvrable(memoire: CleEnMemoire, range: CoffreRange): Promise<boolean> {
  if (!memoire.ouvreLeCoffre) {
    memoire.ouvreLeCoffre =
      (await ouvrirAvecCle(memoire.cle, range.coffre).catch(() => null)) !== null
  }
  return memoire.ouvreLeCoffre
}

/**
 * Télécharge le référentiel administratif et le scelle — sauf si le coffre de
 * l'agent est encore frais : une reconnexion sur le même poste ne coûte aucun
 * export (S314). `forcer` passe outre, pour le bouton « Charger maintenant ».
 */
export async function synchroniser(
  agent: Agent,
  { forcer = false }: { readonly forcer?: boolean } = {},
): Promise<ResultatSynchro> {
  await preparation
  const existant = await lireCoffre(agent.email)
  const coffre = existant !== null
  const memoire = enMemoire
  if (memoire?.email !== cleDe(agent.email)) return { etat: 'sans-cle', coffre }
  if (!forcer && existant !== null && estFrais(existant, Date.now())) {
    if (await ouvrable(memoire, existant)) return { etat: 'fraiche' }
  }
  let referentiel: ReferentielHorsLigne
  try {
    referentiel = await requete('/hors-ligne/referentiel', { schema: schemaReferentielHorsLigne })
  } catch (erreur) {
    if (estErreurApi(erreur) && erreur.statut === 429) {
      const delaiS = erreur.reessayerDansS
      const reessayerDansMs =
        delaiS === undefined || delaiS <= 0 ? DELAI_REESSAI_PAR_DEFAUT_MS : delaiS * 1000
      return { etat: 'limite', coffre, reessayerDansMs }
    }
    return { etat: 'echec', coffre }
  }
  const range = await rangerCoffre({
    email: agent.email,
    nomAffiche: agent.nomAffiche,
    siteNom: agent.siteNom,
    genereLe: referentiel.genereLe,
    scelleLe: new Date().toISOString(),
    nbPersonnes: referentiel.personnes.length,
    coffre: await sceller(memoire.cle, memoire.sel, referentiel),
  })
  if (!range) return { etat: 'echec', coffre }
  memoire.ouvreLeCoffre = true
  return { etat: 'rangee' }
}
